import { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowPathIcon, ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline";

import { Alert } from "../../../components/base/Alert";
import { Button } from "../../../components/base/Button";
import { usePageNameContext } from "../../../contexts/PageTitleContext";
import useCustomers from "../../../data/Customer/useCustomers";
import useTicketInsights from "../../../data/Ticket/useTicketInsights";
import {
  ITicketInsightQuery,
  TicketAiCategory,
} from "../../../models/Tickets/TicketInsight";
import { formatHours } from "../TicketAvgResponseTable";
import {
  EscalationRiskCard,
  PriorityMismatchCard,
  TypeMismatchCard,
} from "./ActionLists";
import AnalyseCard from "./AnalyseCard";
import CategoryBreakdownCard from "./CategoryBreakdownCard";
import { StatCard } from "./InsightWidgets";
import {
  BacklogAgingCard,
  CustomersCard,
  KnowledgeGapsCard,
  TagsCard,
} from "./SecondaryCards";
import SentimentUrgencyCard from "./SentimentUrgencyCard";
import SimilarIssuesCard, { similarityPresets } from "./SimilarIssuesCard";
import TopicsCard from "./TopicsCard";
import WeeklyTrendCard from "./WeeklyTrendCard";
import { categoryLabels } from "./insightMeta";

const rangeOptions: { label: string; days: number }[] = [
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
  { label: "Last 6 months", days: 180 },
  { label: "Last 12 months", days: 365 },
  { label: "All time", days: 0 },
];

const defaultThreshold = similarityPresets[1].value;

/** Scope lives in the URL so a view can be shared or returned to. */
function parseQuery(params: URLSearchParams): ITicketInsightQuery {
  const days = Number(params.get("days"));
  const threshold = Number(params.get("similarity"));

  return {
    customerId: params.get("customerId") ?? undefined,
    days: Number.isFinite(days) && params.has("days") ? days : 90,
    includeResolved: params.get("includeResolved") !== "false",
    similarityThreshold:
      Number.isFinite(threshold) && threshold > 0 ? threshold : defaultThreshold,
  };
}

export default function TicketInsightsPage() {
  const { setInfo } = usePageNameContext();
  const { customers } = useCustomers();
  const [searchParams, setSearchParams] = useSearchParams();

  const query = useMemo(() => parseQuery(searchParams), [searchParams]);
  const { analytics, status, analyse } = useTicketInsights(query);

  useEffect(() => {
    setInfo({
      name: "Ticket Insights",
      desc: "AI review of the support queue: what tickets are really about, which ones are the same issue, and where the risk is.",
      pageTitle: "Ticket Insights",
    });
  }, [setInfo]);

  const updateQuery = (changes: Partial<Record<"customerId" | "days" | "includeResolved" | "similarity", string | undefined>>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([key, value]) => {
      if (value === undefined || value === "") next.delete(key);
      else next.set(key, value);
    });
    setSearchParams(next, { replace: true });
  };

  const data = analytics.data;
  const selectClasses = "rounded border border-gray-300 px-3 py-2 text-sm";

  const analysedTotal = data?.categories.reduce((s, c) => s + c.count, 0) ?? 0;
  const bugs = data?.categories.find((c) => c.category === TicketAiCategory.Bug);
  const features = data?.categories.find(
    (c) => c.category === TicketAiCategory.FeatureRequest,
  );
  const questions = data?.categories.find(
    (c) => c.category === TicketAiCategory.Question,
  );
  const otherCount =
    analysedTotal - (bugs?.count ?? 0) - (features?.count ?? 0) - (questions?.count ?? 0);

  const overallResolution = (() => {
    if (!data) return undefined;
    const withTimes = data.categories.filter(
      (c) => c.avgResolutionHours !== undefined && c.avgResolutionHours !== null && c.done > 0,
    );
    const done = withTimes.reduce((s, c) => s + c.done, 0);
    if (!done) return undefined;
    return withTimes.reduce((s, c) => s + (c.avgResolutionHours as number) * c.done, 0) / done;
  })();

  const customerName = query.customerId
    ? customers.data?.find((c) => c.id === query.customerId)?.name
    : undefined;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="insight-customer" className="mb-1 block text-sm font-medium text-gray-700">
            Customer
          </label>
          <select
            id="insight-customer"
            className={`${selectClasses} w-64`}
            value={query.customerId ?? ""}
            onChange={(e) => updateQuery({ customerId: e.target.value })}
          >
            <option value="">All customers</option>
            {(customers.data ?? []).map((customer) => (
              <option key={customer.id} value={customer.id}>
                {customer.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="insight-range" className="mb-1 block text-sm font-medium text-gray-700">
            Created
          </label>
          <select
            id="insight-range"
            className={selectClasses}
            value={query.days}
            onChange={(e) => updateQuery({ days: e.target.value })}
          >
            {rangeOptions.map((option) => (
              <option key={option.days} value={option.days}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <label className="flex h-[38px] items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={query.includeResolved}
            onChange={(e) =>
              updateQuery({ includeResolved: e.target.checked ? undefined : "false" })
            }
            className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
          />
          Include resolved / closed
        </label>

        <Button
          variant="outlined"
          className="flex h-[38px] items-center gap-1"
          onClick={() => {
            analytics.refetch();
            status.refetch();
          }}
          disabled={analytics.isFetching}
        >
          <ArrowPathIcon className={analytics.isFetching ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
          Refresh
        </Button>

        <Link
          to={query.customerId ? `/tickets?customerId=${query.customerId}&includeResolved=true` : "/tickets?includeResolved=true"}
          className="ml-auto"
        >
          <Button variant="outlined" className="flex h-[38px] items-center gap-1">
            <ChatBubbleLeftRightIcon className="h-4 w-4" />
            Ticket list
          </Button>
        </Link>
      </div>

      <AnalyseCard
        query={query}
        status={status.data}
        isLoading={status.isLoading}
        analyse={analyse}
        onComplete={() => {
          status.refetch();
          analytics.refetch();
        }}
      />

      {analytics.isError ? (
        <Alert
          severity="error"
          message={
            analytics.error instanceof Error
              ? analytics.error.message
              : "Failed to load ticket insights"
          }
        />
      ) : null}

      {analytics.isLoading && !data ? (
        <div className="space-y-4">
          <div className="h-20 animate-pulse rounded-md bg-gray-100" />
          <div className="h-64 animate-pulse rounded-md bg-gray-100" />
          <div className="h-64 animate-pulse rounded-md bg-gray-100" />
        </div>
      ) : null}

      {data ? (
        <>
          <div className="flex flex-wrap gap-2">
            <StatCard
              label={`Analysed tickets${customerName ? ` (${customerName})` : ""}`}
              value={analysedTotal}
              hint={`of ${data.status.totalInScope} in scope`}
              accentClassName="border-l-orange-500"
            />
            <StatCard
              label={categoryLabels[TicketAiCategory.Bug] + "s"}
              value={bugs?.count ?? 0}
              hint={analysedTotal ? `${bugs?.percent ?? 0}% · ${bugs?.active ?? 0} active` : undefined}
              accentClassName="border-l-rose-400"
            />
            <StatCard
              label="Feature requests"
              value={features?.count ?? 0}
              hint={analysedTotal ? `${features?.percent ?? 0}% · ${features?.active ?? 0} active` : undefined}
              accentClassName="border-l-indigo-400"
            />
            <StatCard
              label="Questions"
              value={questions?.count ?? 0}
              hint={analysedTotal ? `${questions?.percent ?? 0}% · ${questions?.active ?? 0} active` : undefined}
              accentClassName="border-l-sky-400"
            />
            <StatCard
              label="Other"
              value={otherCount}
              hint="integration, account, billing, other"
              accentClassName="border-l-gray-400"
            />
            <StatCard
              label="Similar-issue groups"
              value={data.clusters.length}
              hint={
                data.clusters.filter((c) => c.likelyDuplicate).length
                  ? `${data.clusters.filter((c) => c.likelyDuplicate).length} with likely duplicates`
                  : undefined
              }
              accentClassName="border-l-violet-400"
            />
            <StatCard
              label="Negative tone (active)"
              value={`${data.sentiment.activeNegativePercent}%`}
              hint={`${data.sentiment.negative} negative overall`}
              accentClassName="border-l-rose-500"
            />
            <StatCard
              label="Avg resolution"
              value={formatHours(overallResolution)}
              hint="across analysed tickets"
              accentClassName="border-l-green-500"
            />
          </div>

          {analysedTotal === 0 && data.status.isAiConfigured ? (
            <Alert
              severity="info"
              message={
                data.status.totalInScope === 0
                  ? "There are no tickets in this scope. Widen the date range or clear the customer filter."
                  : "Nothing in this scope has been analysed yet. Use the Analyse button above to run the model over the pending tickets."
              }
            />
          ) : null}

          <div className="grid gap-4 xl:grid-cols-2">
            <CategoryBreakdownCard categories={data.categories} />
            <SentimentUrgencyCard sentiment={data.sentiment} urgency={data.urgency} />
          </div>

          <TopicsCard topics={data.topics} hasPreviousPeriod={query.days > 0} />

          <SimilarIssuesCard
            clusters={data.clusters}
            threshold={data.similarityThreshold}
            onThresholdChange={(threshold) =>
              updateQuery({
                similarity:
                  Math.abs(threshold - defaultThreshold) < 0.001
                    ? undefined
                    : String(threshold),
              })
            }
          />

          <div className="grid gap-4 xl:grid-cols-2">
            <EscalationRiskCard risks={data.escalationRisks} />
            <PriorityMismatchCard mismatches={data.priorityMismatches} />
          </div>

          <TypeMismatchCard mismatches={data.typeMismatches} />

          <WeeklyTrendCard weeks={data.weeklyTrend} />

          <div className="grid gap-4 xl:grid-cols-2">
            <CustomersCard
              customers={data.customers}
              onSelectCustomer={(customerId) =>
                updateQuery({
                  customerId: query.customerId === customerId ? undefined : customerId,
                })
              }
            />
            <KnowledgeGapsCard gaps={data.knowledgeGaps} />
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <BacklogAgingCard buckets={data.backlogAging} />
            <TagsCard tags={data.tags} />
          </div>

          <p className="text-xs text-gray-400">
            Generated {new Date(data.generatedAt).toLocaleString()}. Analytics
            are cached for a couple of minutes between analysis runs.
          </p>
        </>
      ) : null}
    </div>
  );
}
